package org.teddapp.project.ui.today

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.flow.MutableStateFlow
import org.teddapp.project.core.CoreReaction
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.TeddRepositories
import androidx.compose.runtime.LaunchedEffect
import org.teddapp.project.core.currentTimeOfDay
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.nowMillis
import org.teddapp.project.sync.SyncStatus
import org.teddapp.project.sync.SyncUiState
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.backup.BackupGap
import org.teddapp.project.ui.backup.BackupGapLevel
import org.teddapp.project.ui.backup.computeRealGap
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.LocalSyncBindings
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.SettingsScreen
import org.teddapp.project.ui.settings.rememberSettingsDrawer

/**
 * Aujourd'hui — point de départ de la journée, sur toutes les plateformes.
 *
 * Répond à trois questions : où en suis-je (score du jour), quoi faire maintenant
 * (tâches et suggestion focus), où en est l'argent du mois. Compact : une colonne
 * défilante et la barre du bas. Étendu : rail, hero, tendance focus puis grille 2:1.
 */
@Composable
fun TodayScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val model = rememberTodayModel(repos)
    val coreSession = LocalCoreSession.current
    // Le noyau rattrape son instantané et charge le fil à la première ouverture.
    LaunchedEffect(coreSession) {
        coreSession?.start(
            timeOfDay = currentTimeOfDay(nowMillis()),
            fatigue = "moderate",
            focusStreak = model.focusMinutesToday / 25,
            physicalReady = true,
        )
    }
    WithWindowClass(modifier = modifier) { wc ->
        when (wc) {
            WindowClass.Compact -> TodayCompactLayout(model, onNavigate)
            else -> TodayExpandedLayout(model, onNavigate)
        }
    }
}

/** Écart de sauvegarde calculé en direct depuis l'état du SyncManager. */
@Composable
private fun rememberBackupGap(): BackupGap {
    val bindings = LocalSyncBindings.current
    val state by (
        bindings?.state ?: remember {
            MutableStateFlow(
                SyncUiState(
                    enabled = false,
                    status = SyncStatus.Idle,
                    lastSyncAt = null,
                    pendingChanges = 0,
                    errorMessage = null,
                ),
            )
        }
        ).collectAsState()
    return remember(bindings, state.lastSyncAt) {
        computeRealGap(cloudAvailable = bindings != null, lastSyncAt = state.lastSyncAt, now = nowMillis())
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun TodayExpandedLayout(model: TodayModel, onNavigate: (Section) -> Unit) {
    val appSettings = LocalAppSettings.current
    val core = LocalCoreSession.current
    val coreRecommendation by (core?.recommendation ?: remember { MutableStateFlow(null) }).collectAsState()
    val coreReactions by (core?.reactions ?: remember { MutableStateFlow(emptyList<CoreReaction>()) }).collectAsState()
    val coreSnapshot by (core?.snapshot ?: remember { MutableStateFlow(null) }).collectAsState()
    val deadlines = remember(model.skills, coreSnapshot) { deadlineReminders(model.skills, coreSnapshot, TODAY_ISO) }
    val loanDeadlines = remember(model.loans, model.loanPayments) { loanReminders(model.loans, model.loanPayments, TODAY_ISO) }
    val gap = rememberBackupGap()
    val drawer = rememberSettingsDrawer()
    val h = 36.dp
    SettingsDrawerHost(drawer, onNavigate = onNavigate) {
        Row(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            TeddNavRail(active = Section.Today, onNavigate = onNavigate, onSettingsClick = drawer::open)
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState())
                    .padding(start = TeddSpace.S8, end = TeddSpace.S8, top = TeddSpace.S8, bottom = TeddSpace.S12),
                verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
            ) {
                if (gap.level != BackupGapLevel.Ok) {
                    GapStrip(level = gap.level, message = gap.message, horizontalPadding = 0.dp) { onNavigate(Section.Backup) }
                }
                TodayBento(
                    model = model,
                    coreLabel = coreRecommendation?.label,
                    reactions = coreReactions,
                    onDismissReaction = { core?.dismiss(it) },
                    onNavigate = onNavigate,
                    deadlines = deadlines,
                    loanDeadlines = loanDeadlines,
                )
            }
        }
    }
    TeddSnackbarHost(state = model.snackbar)
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun TodayCompactLayout(model: TodayModel, onNavigate: (Section) -> Unit) {
    val appSettings = LocalAppSettings.current
    val core = LocalCoreSession.current
    val coreRecommendation by (core?.recommendation ?: remember { MutableStateFlow(null) }).collectAsState()
    val coreReactions by (core?.reactions ?: remember { MutableStateFlow(emptyList<CoreReaction>()) }).collectAsState()
    val coreSnapshot by (core?.snapshot ?: remember { MutableStateFlow(null) }).collectAsState()
    val deadlines = remember(model.skills, coreSnapshot) { deadlineReminders(model.skills, coreSnapshot, TODAY_ISO) }
    val loanDeadlines = remember(model.loans, model.loanPayments) { loanReminders(model.loans, model.loanPayments, TODAY_ISO) }
    val gap = rememberBackupGap()
    var showSettings by remember { mutableStateOf(false) }
    val h = 16.dp

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            TodayCompactHeader(onOpenSettings = { showSettings = true }, onQuickCapture = { model.quickOpen = !model.quickOpen })
            Column(
                modifier = Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                if (model.quickOpen) TodayQuickCapture(model, horizontalPadding = h)
                Column(modifier = Modifier.padding(horizontal = h), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                    TodayRecommendation(
                        reco = model.recommendation,
                        coreLabel = coreRecommendation?.label,
                        onStart = { onNavigate(Section.Focus) },
                        onAlternate = { onNavigate(Section.Focus) },
                    )
                    TodayThreeNumbers(
                        focusMinutes = model.focusMinutesToday,
                        crossings = model.crossingsToday,
                        rationalRate = model.rationalRateMonth,
                        cardsDue = model.cardsDue,
                        onReview = { onNavigate(Section.Focus) },
                    )
                    // Sur téléphone, une carte : les dates butoir proches ou passées.
                    TodayDeadlinesCard(deadlines, onOpen = { onNavigate(Section.Skills) }, loans = loanDeadlines, onOpenFinance = { onNavigate(Section.Finance) })
                    if (coreReactions.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(18.dp))
                        TodayReactionFeed(reactions = coreReactions, onDismiss = { core?.dismiss(it) })
                    }
                }
                if (gap.level != BackupGapLevel.Ok) {
                    GapStrip(level = gap.level, message = gap.message, horizontalPadding = h) { onNavigate(Section.Backup) }
                }
                Column(modifier = Modifier.padding(horizontal = h), verticalArrangement = Arrangement.spacedBy(14.dp)) {
                    TasksCard(tasks = model.pool, onToggle = model::toggleTask, onOpenPool = { onNavigate(Section.Focus) })
                    TodayMoneyCard(model, appSettings.hideBalance, { appSettings.applyHideBalance(!appSettings.hideBalance) }) {
                        onNavigate(Section.Finance)
                    }
                    SectionLabel("Tendance")
                    InsightsRow(focusMin7 = model.focusMin7, weekLabels = model.weekLabels)
                    Spacer(modifier = Modifier.height(12.dp))
                }
            }
            TeddBottomNav(active = Section.Today, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }
        if (showSettings) {
            Dialog(
                onDismissRequest = { showSettings = false },
                properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = false),
            ) { SettingsScreen(onClose = { showSettings = false }, onOpenBackup = { showSettings = false; onNavigate(Section.Backup) }, modifier = Modifier.fillMaxSize()) }
        }
        TeddSnackbarHost(state = model.snackbar)
    }
}

// ──────────────────────────────────────────────────────────────────────
// Communs
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun SectionLabel(text: String) {
    Text(
        text = text.uppercase(),
        color = TeddTokens.InkMuted,
        fontSize = 11.sp,
        fontWeight = FontWeight.Bold,
        letterSpacing = 0.7.sp,
    )
}

@Composable
private fun TodayMoneyCard(model: TodayModel, hideBalance: Boolean, onToggleHide: () -> Unit, onOpenFinance: () -> Unit) {
    val courant = model.courant
    val (income, expense) = model.monthStats
    MoneyCard(
        courantName = courant?.name ?: "Compte courant",
        courantBalance = courant?.balance ?: 0L,
        courantColorHex = courant?.color ?: "#6366F1",
        income = income,
        expense = expense,
        balance30 = model.balance30,
        hideBalance = hideBalance,
        onToggleHide = onToggleHide,
        onOpenFinance = onOpenFinance,
    )
}
